import { absCents, isValidCents, sumCents } from '../../shared/money/cents.js'
import type { Cents } from '../../shared/money/cents.js'
import type { BreakdownItem } from './breakdown.js'
import type { Transaction } from './transaction.js'

/**
 * O invariante do resto, de `Docs/02-Dominio/Breakdown.md`:
 *
 * **`soma(itens) + resto ≡ |valor da transação|`. Sempre.**
 *
 * O resto **não é erro nem estado inválido** — é a parte ainda não detalhada, e
 * ela fica na `enrichment.categoryId` da própria transação. É por isso que o
 * requisito "não é obrigatório usar subcategorias" cai de graça:
 *
 * - zero itens → resto = 100%, a transação está simplesmente na sua categoria.
 *   É o caso normal, e o mais comum;
 * - alguns itens → o resto continua na categoria-pai. Detalhamento parcial é
 *   válido: você separou os R$ 200 de combustível e não se importa com os
 *   outros R$ 100;
 * - itens somando o valor cheio → resto = 0.
 *
 * **Tudo aqui é em módulo.** `BreakdownItem.amountCents` é sempre positivo e o
 * sinal mora em `Transaction.source.amountCents` — ver `@shared/money/cents`.
 * Uma despesa de R$ 312,45 e uma receita de R$ 312,45 produzem exatamente o
 * mesmo resumo, e é assim que uma entrada é detalhável sem uma segunda regra.
 */

/**
 * **Derivado, não redeclarado** — decisão 89, o mesmo caminho que `CashImpact`
 * seguiu na Task 4. Repetir os três campos aqui compilaria limpo mesmo já
 * divergente: dois objetos de mesma forma são mutuamente atribuíveis, o barrel
 * não tem o que acusar porque `Transaction['enrichment']['breakdown']` é um
 * tipo inline e anônimo, e `tsc -b --noEmit` sairia em 0. O alias torna a
 * divergência impossível em vez de detectável.
 *
 * As travas de identidade — a que casa com `Transaction` e a que casa com a
 * forma de três campos — estão em `breakdown-remainder.test.ts`.
 */
export type BreakdownSummary = Transaction['enrichment']['breakdown']

/**
 * A soma dos itens passou do valor da transação. Erro com tipo próprio, e não
 * `Error` genérico, porque é a única falha desta função que quem chama sabe
 * **o que fazer com**: a UI de detalhamento a traduz em "faltam R$ x" ao lado
 * do campo, e a Cloud Function de recálculo a distingue de dado corrompido.
 * Item inválido continua sendo `Error` — ninguém conserta um `NaN` do Firestore
 * mexendo no valor de um item.
 */
export class BreakdownExceedsTotalError extends Error {}

/**
 * O resumo denormalizado que vai para `Transaction.enrichment.breakdown`.
 *
 * **Valida os VALORES na entrada, e é de propósito.** Quem chama é a Cloud
 * Function que recalcula o resumo a partir de documentos do Firestore, e
 * `@shared/money/cents` é explícito em dizer que `absCents` e `sumCents` não
 * validam nada — "quem recebe dado de fora chama `isValidCents` primeiro". Sem
 * as duas guardas abaixo, um `NaN` vindo do banco atravessa calado
 * (`NaN > NaN` é `false`) e sai gravado como `remainderCents: NaN` na
 * transação, que é dado financeiro corrompido em silêncio.
 *
 * **A FORMA do argumento não é verificada, e isso é escolha.** `items` não passa
 * por `Array.isArray`: quem monta a lista é o chamador, com um `map` sobre o
 * snapshot da consulta, e o tipo já diz o que é. Um não-array chegando aqui
 * morre num `TypeError` do `for...of` — feio, mas **alto**, e nenhum resumo
 * errado é gravado. O que precisa de guarda é o que falha em **silêncio**, e
 * isso é o valor de cada item, não o formato da coleção.
 *
 * A ordem das checagens importa: item inválido é reprovado **antes** da soma,
 * para que a mensagem aponte para o dado errado e não para um total que só
 * ficou errado por causa dele.
 *
 * A soma nunca perde exatidão sem avisar: se os itens estouram o inteiro
 * seguro, o acumulado já é `>= 2^53`, portanto maior que qualquer total válido,
 * e o excesso é lançado em vez de um resto inventado.
 */
export function breakdownRemainder(
  transactionAmountCents: Cents,
  items: Pick<BreakdownItem, 'amountCents' | 'categoryId'>[],
): BreakdownSummary {
  if (!isValidCents(transactionAmountCents)) {
    throw new Error(
      `breakdown: o valor da transação precisa ser inteiro em centavos, recebi ${String(transactionAmountCents)}`,
    )
  }

  for (const item of items) {
    if (!isValidCents(item.amountCents) || item.amountCents <= 0) {
      throw new Error(
        `breakdown: item precisa ter amountCents inteiro e positivo — o sinal mora na transação —, recebi ${String(item.amountCents)}`,
      )
    }
  }

  const totalCents = absCents(transactionAmountCents)
  const allocatedCents = sumCents(items.map((item) => item.amountCents))

  if (allocatedCents > totalCents) {
    throw new BreakdownExceedsTotalError(
      `breakdown: os itens somam ${String(allocatedCents)} e a transação é de ${String(totalCents)} em módulo`,
    )
  }

  // **O mapa por categoria sai da MESMA passada.** Uma segunda função somando
  // os mesmos itens seria um segundo lugar para errar o mesmo invariante — e o
  // cabeçalho deste arquivo existe justamente porque essa conta tem de ter um
  // dono só. `sumCents` por chave, e não `+=` solto: o tipo `Cents` é o que
  // impede um float de entrar.
  const byCategory: Record<string, Cents> = {}
  for (const item of items) {
    byCategory[item.categoryId] = sumCents([byCategory[item.categoryId] ?? 0, item.amountCents])
  }

  return {
    itemCount: items.length,
    allocatedCents,
    remainderCents: totalCents - allocatedCents,
    byCategory,
  }
}
